"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useId, useState, type FormEvent } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PasswordInput } from "./PasswordInput"
import { useAuth } from "@/hooks/useAuth"
import { useI18n } from "@/i18n"
import type { AuthMode } from "./authMode"
import { SocialSignInButtons } from "./SocialSignInButtons"

interface SignInFormProps {
  onModeChange: (mode: AuthMode, email?: string) => void
  onSuccess: () => void
}

export function SignInForm({ onModeChange, onSuccess }: SignInFormProps) {
  const { t } = useI18n()
  const { signIn, errorKey, clearError } = useAuth()
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const emailId = useId()
  const passwordId = useId()

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    clearError()
    setSubmitting(true)
    try {
      await signIn(email, password)
      onSuccess()
    } catch {
      // errorKey is already set by the auth context.
    } finally {
      setSubmitting(false)
    }
  }

  // data-testid: the landing page now also has a contact-form Email input,
  // so E2E must scope auth fills to this form (2026-08-25).
  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={handleSubmit}
      noValidate
      data-testid="signin-form"
    >
      {/* Config-flagged social sign-in — renders nothing until enabled. */}
      <SocialSignInButtons disabled={submitting} />

      <div className="flex flex-col gap-1.5">
        <label htmlFor={emailId} className="text-sm font-medium text-ink">
          {t("auth.emailLabel")}
        </label>
        <Input
          id={emailId}
          name="email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={event => setEmail(event.target.value)}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor={passwordId} className="text-sm font-medium text-ink">
          {t("auth.passwordLabel")}
        </label>
        <PasswordInput
          id={passwordId}
          name="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={event => setPassword(event.target.value)}
        />
      </div>

      {errorKey && (
        <p role="alert" className="text-sm text-destructive">
          {t(errorKey)}
        </p>
      )}

      <Button type="submit" disabled={submitting} className="min-h-11">
        {t("auth.signInButton")}
      </Button>

      {/* Centered, matching the "Need an account?" line below. */}
      <p className="text-center text-sm">
        <button
          type="button"
          className="text-green underline underline-offset-2"
          onClick={() => onModeChange("forgot", email)}
        >
          {t("auth.forgotPasswordLink")}
        </button>
      </p>

      <p className="text-center text-sm text-muted-foreground">
        {t("auth.needAccount")}{" "}
        <button
          type="button"
          className="font-medium text-green underline underline-offset-2"
          onClick={() => onModeChange("signUp")}
        >
          {t("auth.signUpTitle")}
        </button>
      </p>
    </form>
  )
}
